import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, Fragment as _Fragment, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "piped-value__pill__close" };
const _hoisted_2 = ["aria-expanded"];
const _hoisted_3 = ["aria-label"];
const _hoisted_4 = { class: "piped-value__grid" };
const _hoisted_5 = { class: "piped-value__grid__tag" };
import { ref, computed, onMounted, onUnmounted, inject, onBeforeMount, nextTick } from "vue";
import { storeToRefs } from "pinia";
import { useElementHover, useElementBounding, useDebounceFn, useIntersectionObserver, watchDebounced } from "@vueuse/core";
import { createPipingStore } from "../store";
import { TaskType } from "../types";
// Components
import SvgIcon from "@/common/components/SvgIcon.vue";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'PipedValueEdit',
    props: {
        id: { default: () => crypto.randomUUID() },
        studyId: {},
        pipeId: {},
        activityId: {},
        taskId: {},
        taskSubId: {},
        responseType: {},
        responseSubId: {},
        element: {},
        destroyFn: {}
    },
    setup(__props) {
        const i18n = inject("i18n");
        const props = __props;
        const target = ref(null);
        const tempTarget = ref(null);
        const menu = ref(null);
        const pill = ref(null);
        const elementRef = ref(props.element);
        const popoverId = `popover-${props.id}`;
        const targetId = `target-${props.id}`;
        const pillId = `pill-${props.id}`;
        const pipingStore = createPipingStore({ studyId: props.studyId, id: props.pipeId ?? -1 });
        const { config } = storeToRefs(pipingStore);
        const activity = computed(() => config.value?.activities?.find((a) => a.id === props.activityId));
        const task = computed(() => activity.value?.tasks?.find((t) => t.id === props.taskId));
        const blank = computed(() => task.value?.subOptions?.find((b) => "id" in b && b.id === props.taskSubId));
        const criteria = computed(() => {
            if (task.value) {
                if (task.value.type === TaskType.Grid) {
                    const row = task.value.rows?.find((r) => r.id === props.responseSubId)?.name;
                    const column = task.value.columns?.find((r) => r.id === props.responseSubId)?.name;
                    if (props.responseType == 1)
                        return i18n.t("All selected rows");
                    else if (props.responseType == 2)
                        return i18n.t("Selected for a specific row") + ": " + row;
                    else if (props.responseType == 3)
                        return i18n.t("Not selected for a specific row") + ": " + row;
                    else if (props.responseType == 4)
                        return i18n.t("All selected columns");
                    else if (props.responseType == 5)
                        return i18n.t("Selected for a specific column") + ": " + column;
                    else if (props.responseType == 6)
                        return i18n.t("Not selected for a specific column") + ": " + column;
                }
                else if (task.value.type === TaskType.Sort) {
                    const group = task.value.groups?.find((r) => r.id === props.responseSubId)?.name;
                    if (props.responseType == 1)
                        return i18n.t("Cards sorted into a particular group") + ": " + group;
                    else if (props.responseType == 2)
                        return i18n.t("Cards not sorted into a particular group") + ": " + group;
                    else if (props.responseType == 3)
                        return i18n.t("All sorted cards");
                    else if (props.responseType == 4)
                        return i18n.t("All unsorted cards");
                }
                else {
                    if (props.responseType == 1)
                        return i18n.t("Selected Choices");
                    else if (props.responseType == 2)
                        return i18n.t("Unselected Choices");
                }
            }
            else {
                return activity.value ? i18n.t("Related task deleted") : i18n.t("Related activity deleted");
            }
            return undefined;
        });
        const loading = computed(() => !config.value);
        const pillError = computed(() => {
            return !loading.value && !task.value;
        });
        // Handle hover events
        const isPillHovered = useElementHover(target, {
            delayEnter: 250,
            delayLeave: 250
        });
        const isMenuHovered = useElementHover(menu, {
            delayLeave: 250
        });
        const showPopover = computed(() => isPillHovered.value || isMenuHovered.value);
        const pillBounds = useElementBounding(props.element);
        const boxRef = props.element.closest(".redactor-box");
        const rteBounds = useElementBounding(boxRef);
        const x = computed(() => {
            return pillBounds.x.value - rteBounds.x.value;
        });
        const y = computed(() => pillBounds.y.value - rteBounds.y.value);
        const width = computed(() => pillBounds.width.value);
        const height = computed(() => pillBounds.height.value);
        const menuTop = ref("auto");
        const menuLeft = ref("auto");
        const menuRight = ref("auto");
        const menuArrowLeft = ref("auto");
        const menuArrowRight = ref("auto");
        const resetMenuPosition = () => {
            menuTop.value = "auto";
            menuLeft.value = "auto";
            menuRight.value = "auto";
            menuArrowLeft.value = "auto";
            menuArrowRight.value = "auto";
        };
        const updateMenuPosition = () => {
            const el = menu.value;
            if (!el)
                return;
            const rect = el.getBoundingClientRect();
            const { left, right } = rect;
            const viewportWidth = window.innerWidth;
            menuTop.value = `${pillBounds.y.value + (height.value ?? 0) + 4}px`;
            if (left <= 13 || pillBounds.x.value <= 13) {
                menuLeft.value = "13px";
                menuArrowLeft.value = `${pillBounds.x.value + width.value / 2 - 23}px`; // 13px padding + 10px arrow offset
                menuArrowRight.value = "";
            }
            else if (right >= viewportWidth - 13) {
                menuRight.value = "13px";
                menuArrowRight.value = `${viewportWidth - pillBounds.x.value - width.value / 2 - 23}px`; // 13px padding + 10px arrow offset
                menuArrowLeft.value = "";
            }
            else {
                menuLeft.value = `${pillBounds.x.value - el.offsetWidth / 2 + (pillBounds.width.value ?? 0) / 2}px`;
                menuArrowLeft.value = "calc(50% - 10px)";
                menuArrowRight.value = "";
            }
        };
        const pillClasses = computed(() => {
            return {
                "piped-value__pill": true,
                "piped-value__pill__error": pillError.value,
                "piped-value__pill__loading": loading.value
            };
        });
        // Observer for dynamic visibility
        const debouncedUpdateMenuPosition = useDebounceFn(updateMenuPosition, 50);
        useIntersectionObserver(menu, debouncedUpdateMenuPosition, {
            root: null,
            threshold: 0
        });
        // Watch popover visibility
        watchDebounced(showPopover, async (isVisible) => {
            if (isVisible) {
                await nextTick();
                updateMenuPosition();
            }
            else {
                resetMenuPosition();
            }
        }, { debounce: 50 });
        const pillSized = ref(false);
        const resizePill = () => {
            if (elementRef.value && pill.value) {
                elementRef.value.style.width = `${width.value}px`;
                elementRef.value.style.height = `${height.value}px`;
                pillSized.value = true;
            }
            rteBounds.update();
            pillBounds.update();
        };
        const movePill = () => {
            if (!pill.value)
                return;
            if (!elementRef.value?.checkVisibility()) {
                pill.value.style.visibility = "hidden";
                return;
            }
            else if (pill.value?.style.visibility == "hidden") {
                pill.value.style.visibility = "visible";
            }
            elementRef.value.contentEditable = "false";
            rteBounds.update();
            pillBounds.update();
        };
        const ensureNonBreakingSpaces = (element) => {
            const parent = element.parentNode;
            if (parent && parent.nodeType === Node.ELEMENT_NODE) {
                const parentElement = parent;
                // Get the text nodes before and after the element
                const previousSibling = element.previousSibling;
                const nextSibling = element.nextSibling;
                // Ensure `&nbsp;` before the element
                if (!previousSibling ||
                    previousSibling.nodeType !== Node.TEXT_NODE ||
                    !previousSibling.textContent?.endsWith("\u200B")) {
                    const spaceBefore = document.createTextNode("\u200B");
                    parentElement.insertBefore(spaceBefore, element);
                }
                // Ensure `&nbsp;` after the element
                if (!nextSibling || nextSibling.nodeType !== Node.TEXT_NODE || !nextSibling.textContent?.startsWith("\u200B")) {
                    const spaceAfter = document.createTextNode("\u200B");
                    parentElement.insertBefore(spaceAfter, element.nextSibling);
                }
            }
        };
        const observeElementUpdate = (element, parent) => {
            const observer = new MutationObserver((mutationsList) => {
                ensureNonBreakingSpaces(element);
            });
            observer.observe(parent, { childList: true, subtree: true });
            return () => observer.disconnect();
        };
        // Observe element removal
        const observeElementRemoval = (element, onRemove) => {
            const parent = document.body; //props.element.closest('[contenteditable="true"]');
            if (!parent) {
                pill.value?.remove();
                onRemove();
                return;
            }
            const observer = new MutationObserver((mutationsList) => {
                if (!parent.contains(element)) {
                    onRemove();
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
            return () => observer.disconnect();
        };
        onBeforeMount(() => {
            if (elementRef.value) {
                elementRef.value.style.width = "auto";
                elementRef.value.style.height = "auto";
                ensureNonBreakingSpaces(elementRef.value);
            }
        });
        onMounted(() => {
            const parent = props.element.closest('[contenteditable="true"]');
            setTimeout(resizePill, 2000);
            const token = setInterval(movePill, 10);
            const stopObserving = observeElementRemoval(props.element, props.destroyFn);
            const stopObservingUpdate = parent && observeElementUpdate(props.element, parent);
            window.addEventListener("resize", debouncedUpdateMenuPosition, { passive: true });
            window.addEventListener("scroll", debouncedUpdateMenuPosition, { passive: true });
            onUnmounted(() => {
                stopObserving?.();
                stopObservingUpdate?.();
                pill.value?.remove();
                clearInterval(token);
                window.removeEventListener("resize", debouncedUpdateMenuPosition);
                window.removeEventListener("scroll", debouncedUpdateMenuPosition);
            });
        });
        const deletePill = () => {
            props.element.remove();
            props.destroyFn();
            pill.value?.remove();
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (!pillSized.value)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        ref_key: "tempTarget",
                        ref: tempTarget,
                        class: "piped-value__pill",
                        style: { "visibility": "hidden" }
                    }, [
                        _createTextVNode(_toDisplayString(criteria.value) + " ", 1),
                        _createElementVNode("span", _hoisted_1, [
                            _createVNode(SvgIcon, { icon: "icon-close-3" })
                        ])
                    ], 512))
                    : _createCommentVNode("", true),
                (_unref(boxRef))
                    ? (_openBlock(), _createBlock(_Teleport, {
                        key: 1,
                        to: _unref(boxRef)
                    }, [
                        _createElementVNode("div", {
                            id: pillId,
                            ref_key: "pill",
                            ref: pill,
                            style: _normalizeStyle(`position: absolute; left:${x.value}px; top: ${y.value}px; width: ${width.value}px; height: ${height.value}px;`),
                            class: "piped-value"
                        }, [
                            _createElementVNode("span", {
                                id: targetId,
                                ref_key: "target",
                                ref: target,
                                role: "button",
                                class: _normalizeClass(pillClasses.value),
                                "aria-haspopup": "true",
                                "aria-expanded": showPopover.value,
                                "aria-controls": popoverId,
                                tabindex: "0",
                                onClick: _cache[0] || (_cache[0] = _withModifiers(() => { }, ["stop"]))
                            }, [
                                (loading.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createTextVNode(_toDisplayString(_ctx.$t("Loading")) + "... ", 1)
                                    ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createTextVNode(_toDisplayString(criteria.value) + " ", 1),
                                        _createElementVNode("span", {
                                            role: "button",
                                            class: "piped-value__pill__close",
                                            "aria-label": _ctx.$t('Remove'),
                                            tabindex: "0",
                                            onClick: _withModifiers(deletePill, ["stop"])
                                        }, [
                                            _createVNode(SvgIcon, { icon: "icon-close-3" })
                                        ], 8, _hoisted_3)
                                    ], 64))
                            ], 10, _hoisted_2),
                            _createVNode(_Transition, { name: "fade" }, {
                                default: _withCtx(() => [
                                    (showPopover.value)
                                        ? (_openBlock(), _createElementBlock("div", {
                                            key: 0,
                                            id: popoverId,
                                            ref_key: "menu",
                                            ref: menu,
                                            class: "piped-value__popover",
                                            role: "menu",
                                            "aria-labelledby": targetId,
                                            style: _normalizeStyle(`top: ${menuTop.value}; left: ${menuLeft.value}; right: ${menuRight.value};`)
                                        }, [
                                            _createElementVNode("div", {
                                                class: "piped-value__popover__arrow",
                                                style: _normalizeStyle(`left: ${menuArrowLeft.value}; right: ${menuArrowRight.value};`)
                                            }, null, 4),
                                            _createElementVNode("div", _hoisted_4, [
                                                _createElementVNode("div", null, _toDisplayString(_ctx.$t("Activity")), 1),
                                                _createElementVNode("div", null, [
                                                    _createElementVNode("span", {
                                                        class: _normalizeClass(["piped-value__grid__tag", { 'piped-value__grid__tag__error': !activity.value }])
                                                    }, _toDisplayString(activity.value ? activity.value.name : _ctx.$t("Related activity deleted")), 3)
                                                ]),
                                                _createElementVNode("div", null, _toDisplayString(_ctx.$t("Task")), 1),
                                                _createElementVNode("div", null, [
                                                    _createElementVNode("span", {
                                                        class: _normalizeClass(["piped-value__grid__tag", { 'piped-value__grid__tag__error': !task.value }])
                                                    }, _toDisplayString(task.value ? task.value.name : _ctx.$t("Related task deleted")), 3)
                                                ]),
                                                (blank.value)
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        _createElementVNode("div", null, _toDisplayString(_ctx.$t("Blank")), 1),
                                                        _createElementVNode("div", null, [
                                                            _createElementVNode("span", _hoisted_5, _toDisplayString(blank.value.name), 1)
                                                        ])
                                                    ], 64))
                                                    : _createCommentVNode("", true)
                                            ])
                                        ], 4))
                                        : _createCommentVNode("", true)
                                ]),
                                _: 1
                            })
                        ], 4)
                    ], 8, ["to"]))
                    : _createCommentVNode("", true)
            ], 64));
        };
    }
});
